'use client';
import React, { useState } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { DialogHeader } from '@src/v3/components/coreUI/dialog';
import Icon from '@src/v3/components/coreUI/icon';
import Button from '@src/v3/components/coreUI/button';
import OtpInput from '@src/v3/components/coreUI/otpInput';
import GradientPanelEmailVerification from '@src/v3/assets/svg/gradientPanelEmailVerification';
import EmailConfirmationCard from '@src/v3/assets/svg/emailConfirmationCard';
import {
  RegisterStepProps,
  RegisterFormValues,
  UserType,
} from '@src/v3/interfaces/components/registerModal';
import { emailOtpValidationSchema } from '@src/v3/schemas/components/register/index';
import {
  OTP_VARIANTS,
  REGISTER_STEPS,
  FORM_FIELD_NAMES,
} from '@src/v3/constants/configs';
import { ERROR_MESSAGES } from '@src/v3/constants/messages';
import EmailConfirmationGraphic from '@src/v3/assets/svg/emailConfirmationGraphic';
import { cn } from '@src/v3/lib/utils';

const EmailVerificationStep: React.FC<RegisterStepProps> = ({
  email = '',
  emailResendTimer = 0,
  onEmailResend,
  canResendEmail = false,
  onStepChange,
  onClose,
  isEmailVerificationLoading = false,
  onEmailVerificationSubmit,
  title = 'Check your Email',
  hideRightPanel = false,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (
    values: RegisterFormValues,
    { setSubmitting, setFieldValue }: FormikHelpers<RegisterFormValues>
  ) => {
    if (!values.code || values.code.trim() === '') {
      setSubmitting(false);
      return;
    }
    if (!/^\d{6}$/.test(values.code)) {
      setSubmitting(false);
      return;
    }

    setErrorMessage(''); // Clear previous error
    try {
      await onEmailVerificationSubmit?.(values.code);
    } catch (error) {
      const apiError = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMsg =
        apiError?.response?.data?.message ||
        apiError?.message ||
        ERROR_MESSAGES.otpCodeInvalid;
      setErrorMessage(errorMsg);
      // Clear OTP input value on error
      setFieldValue(FORM_FIELD_NAMES.CODE, '');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col md:flex-row">
      <div
        className={`w-full h-full self-stretch px-0 pt-0 pb-0 bg-white rounded-none flex flex-col items-start relative ${
          hideRightPanel
            ? 'md:rounded-2xl md:h-[752px]'
            : 'md:rounded-l-2xl md:px-2.5 md:pt-2.5 md:h-[790px] md:pb-8'
        }`}
      >
        {/* Mobile Header */}
        <div className="md:hidden w-full">
          <DialogHeader className="px-5 pt-6 pb-5 flex flex-col gap-3 items-start">
            <div className="self-stretch flex flex-col gap-4 items-start w-full">
              <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className="flex-1 justify-start text-black text-2xl font-medium font-geist leading-9 tracking-[-0.48px]">
                  {title}
                </div>
                <span
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Icon
                    iconName="crossicon"
                    iconProps={{ className: 'w-5 h-5 text-gray-600' }}
                  />
                </span>
              </div>
              <div className="self-stretch flex flex-col gap-0 items-start">
                <div className="flex-1 justify-start text-gray-800 text-base font-normal font-geist leading-7">
                  We sent a code to your email:
                </div>
                <div className="flex-1 justify-start text-neutral-900 text-base font-normal font-geist leading-7">
                  {email}
                </div>
                <div className="flex-1 justify-start text-base font-normal font-geist leading-7">
                  <Button
                    type="button"
                    variant="link"
                    className="text-focus underline hover:opacity-75 h-auto p-0"
                    onClick={() => onStepChange?.(REGISTER_STEPS.UPDATE_EMAIL)}
                  >
                    Try a different email?
                  </Button>
                </div>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:flex self-stretch flex-col justify-start items-start gap-2">
          <div
            className={cn(
              'self-stretch px-8 pb-5 bg-white inline-flex flex-col justify-center items-start gap-3',
              hideRightPanel ? 'pt-11' : 'pt-16'
            )}
          >
            <div
              className={cn(
                'self-stretch flex flex-col justify-start items-start',
                hideRightPanel ? 'gap-0' : 'gap-4'
              )}
            >
              <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className="flex-1 justify-start text-gray-1000 text-xl md:text-2xl font-medium font-geist leading-7 md:leading-9">
                  {title}
                </div>
              </div>
              <div className="flex flex-col items-center gap-y-1">
                <div className="flex-1 justify-start">
                  <span
                    className={cn(
                      'text-base font-normal font-geist leading-6',
                      hideRightPanel ? 'text-gray-800' : 'text-gray-1000 '
                    )}
                  >
                    We sent a code to your email:{' '}
                  </span>
                  <span className="text-gray-1000 text-base font-normal font-geist leading-6">
                    {email}
                  </span>
                </div>
                <div className="w-full flex justify-start">
                  <Button
                    type="button"
                    variant="link"
                    className="text-focus text-base font-normal font-geist underline leading-6 hover:opacity-75 h-auto p-0"
                    onClick={() => onStepChange?.(REGISTER_STEPS.UPDATE_EMAIL)}
                  >
                    Try a different Email?
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Formik
          initialValues={{
            phone: '',
            code: '',
            userType: 'renter' as UserType,
          }}
          validationSchema={emailOtpValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, isSubmitting, submitForm }) => (
            <Form className="flex-1 w-full flex flex-col">
              <div className="flex-1 w-full flex flex-col max-sm:overflow-y-auto md:pb-0">
                {/* Mobile Content */}
                <div className="md:hidden px-5 py-3 flex flex-col gap-8 min-h-full">
                  <OtpInput
                    length={6}
                    value={values.code || ''}
                    onChange={(value) => {
                      if (value.length > 1) {
                        const sanitized = value.replace(/\D/g, '').slice(0, 6);

                        handleChange({
                          target: {
                            name: FORM_FIELD_NAMES.CODE,
                            value: sanitized,
                          },
                        });

                        if (sanitized.length === 6) {
                          submitForm();
                        }
                        return;
                      }

                      handleChange({
                        target: { name: FORM_FIELD_NAMES.CODE, value },
                      });

                      if (errorMessage) {
                        setErrorMessage('');
                      }
                    }}
                    onComplete={submitForm}
                    disabled={isEmailVerificationLoading || isSubmitting}
                    error={!!errorMessage}
                    className="flex gap-[10px] items-center justify-start w-full min-w-0"
                    inputClassName="flex-1 min-w-0 h-12 p-2 flex items-center justify-center gap-2.5 rounded-xl border bg-white text-center text-base font-normal font-geist text-gray-800 placeholder:text-gray-800 focus:outline-none"
                    dataAttribute="email-otp-index"
                    variant={OTP_VARIANTS.MOBILE}
                  />

                  {/* Error Message - Mobile */}
                  {errorMessage && (
                    <div className="inline-flex justify-start items-start gap-2">
                      <Icon
                        iconName="warningCircle"
                        iconProps={{
                          className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                        }}
                      />
                      <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                        {errorMessage}
                      </span>
                    </div>
                  )}

                  <div className="self-stretch inline-flex justify-between items-center">
                    <div className="flex-1 justify-start">
                      <span className="text-gray-800 text-base font-normal font-geist leading-6">
                        Didn&apos;t get it? Wait a moment, or <br />
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        disabled={
                          isEmailVerificationLoading ||
                          !canResendEmail ||
                          isSubmitting
                        }
                        onClick={() => {
                          setErrorMessage('');
                          onEmailResend?.();
                        }}
                        className={`text-focus text-base font-normal font-geist underline leading-6 hover:opacity-75 h-auto p-0 ${
                          !canResendEmail
                            ? 'text-gray-800 cursor-not-allowed opacity-50'
                            : ''
                        }`}
                      >
                        Request a New Code
                      </Button>
                    </div>
                    {emailResendTimer > 0 && (
                      <div className="justify-start text-gray-800 text-base font-normal font-geist leading-6 ml-2">
                        {formatTime(emailResendTimer)}
                      </div>
                    )}
                  </div>

                  {/* Email Confirmation Card - Mobile */}
                  <div className="relative overflow-hidden flex-1 -top-3 -mx-5">
                    <EmailConfirmationCard className="absolute -top-5 right-0" />
                  </div>
                </div>

                {/* Desktop Content */}
                <div
                  className={cn(
                    'hidden md:flex self-stretch px-8 flex-col justify-start items-start',
                    hideRightPanel ? 'py-0 gap-6' : 'py-2.5 gap-6 md:gap-8'
                  )}
                >
                  <OtpInput
                    length={6}
                    value={values.code || ''}
                    onChange={(value) => {
                      handleChange({
                        target: { name: FORM_FIELD_NAMES.CODE, value },
                      });
                      if (errorMessage) {
                        setErrorMessage('');
                      }
                    }}
                    onComplete={submitForm}
                    disabled={isEmailVerificationLoading || isSubmitting}
                    error={!!errorMessage}
                    className="inline-flex justify-start items-center gap-2.5"
                    inputClassName="w-12 h-12 p-2 flex items-center justify-center gap-2.5 rounded-xl border bg-white text-center text-base font-normal font-geist placeholder:text-gray-800 focus:outline-none text-gray-1000 tracking-[-0.16px] leading-6"
                    dataAttribute="email-otp-index"
                    variant={OTP_VARIANTS.DESKTOP}
                  />

                  {/* Error Message - Desktop */}
                  {errorMessage && (
                    <div className="self-stretch inline-flex items-start gap-2">
                      <Icon
                        iconName="warningCircle"
                        iconProps={{
                          className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                        }}
                      />
                      <span className="text-red-700 text-sm font-normal font-geist leading-6">
                        {errorMessage}
                      </span>
                    </div>
                  )}

                  <div className="self-stretch inline-flex justify-between items-center">
                    <div className="w-80 justify-start">
                      <span className="text-gray-1000 text-base font-normal font-geist leading-6">
                        Didn&apos;t get it? Wait a moment, or <br />
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        disabled={
                          isEmailVerificationLoading ||
                          !canResendEmail ||
                          isSubmitting
                        }
                        onClick={onEmailResend}
                        className={`text-focus text-base font-normal font-geist underline leading-6 hover:opacity-75 h-auto p-0 ${
                          !canResendEmail
                            ? 'text-gray-1000 cursor-not-allowed opacity-50'
                            : ''
                        }`}
                      >
                        request a new code
                      </Button>
                    </div>
                    <div className="justify-start text-gray-1000 text-sm font-normal font-geist leading-6">
                      {emailResendTimer > 0 ? formatTime(emailResendTimer) : ''}
                    </div>
                  </div>

                  {/* Email Confirmation Card - Desktop v2 (only when useVersion2) */}
                  {hideRightPanel && (
                    <div className="relative overflow-hidden flex-1 mx-auto top-[46px]">
                      <EmailConfirmationGraphic className="w-full h-full" />
                    </div>
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {!hideRightPanel && (
        <div className="hidden md:flex relative flex-col p-2.5 items-start gap-2.5 self-stretch shrink-0 rounded-r-2xl bg-background-200 overflow-hidden w-[550px] h-[790px]">
          <GradientPanelEmailVerification
            className="absolute inset-0 w-full h-full"
            preserveAspectRatio="none"
          />
        </div>
      )}
    </div>
  );
};

export default EmailVerificationStep;
